import React, { useEffect, useState } from 'react';
import { Box, Paper, Toolbar, Typography, FormControl, InputLabel, Select, MenuItem, TextField, Button, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import apiAxiosInstance from '../../utils/axiosInstance';
import { useSearchParams, useNavigate } from 'react-router-dom';

interface AssessmentMeta { id: string; title: string; date: string; }
interface RowCell { score?: number | null; status?: string | null; submittedAt?: string; }
interface Row { studentId: string; firstName: string; lastName: string; group?: string; cells: Record<string, RowCell>; }

const Gradebook: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [groupId, setGroupId] = useState<string>(searchParams.get('groupId') || '');
  const [from, setFrom] = useState<Dayjs | null>(searchParams.get('from') ? (new Date(String(searchParams.get('from'))) as any) : null);
  const [to, setTo] = useState<Dayjs | null>(searchParams.get('to') ? (new Date(String(searchParams.get('to'))) as any) : null);
  const [search, setSearch] = useState<string>(searchParams.get('search') || '');
  const [loading, setLoading] = useState<boolean>(false);
  const [assessments, setAssessments] = useState<AssessmentMeta[]>([]);
  const [rows, setRows] = useState<Row[]>([]);
  const [groups, setGroups] = useState<Array<{ _id: string; name: string }>>([]);

  // Load groups
  useEffect(() => {
    apiAxiosInstance.get('/api/groups').then(res => {
      setGroups((res.data || []).map((g: any) => ({ _id: g._id, name: g.name })));
    }).catch(() => {});
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (groupId) params.groupId = groupId;
      if (from) params.from = from.toDate().toISOString();
      if (to) params.to = to.toDate().toISOString();
      if (search) params.search = search;
      const res = await apiAxiosInstance.get('/api/gradebook/pivot', { params });
      setAssessments(res.data.assessments || []);
      setRows(res.data.rows || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const onApplyFilters = () => {
    const next = new URLSearchParams();
    if (groupId) next.set('groupId', groupId);
    if (from) next.set('from', from.toDate().toISOString());
    if (to) next.set('to', to.toDate().toISOString());
    if (search) next.set('search', search);
    setSearchParams(next);
    fetchData();
  };

  const onExport = () => {
    const qs = new URLSearchParams();
    if (groupId) qs.set('groupId', groupId);
    if (from) qs.set('from', from.toDate().toISOString());
    if (to) qs.set('to', to.toDate().toISOString());
    if (search) qs.set('search', search);
    const url = `/api/gradebook/export.csv?${qs.toString()}`;
    window.location.href = url;
  };

  const dateFmt = (s: string) => {
    const d = new Date(s);
    return `${d.getMonth() + 1}/${d.getDate()}`;
  };

  const columnWidth = 40; // 20% wider than before for grades
  const studentColWidth = 220;

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>Gradebook</Typography>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Toolbar disableGutters sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="group-label">Group</InputLabel>
            <Select labelId="group-label" label="Group" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              <MenuItem value="">
                <em>All Groups</em>
              </MenuItem>
              {groups.map(g => (
                <MenuItem key={g._id} value={g._id}>{g.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker 
              label="From" 
              value={from} 
              onChange={(v) => setFrom(v)} 
              renderInput={(params) => <TextField size="small" {...params} />} 
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker 
              label="To" 
              value={to} 
              onChange={(v) => setTo(v)} 
              renderInput={(params) => <TextField size="small" {...params} />} 
            />
          </LocalizationProvider>
          <TextField size="small" label="Search students" value={search} onChange={(e) => setSearch(e.target.value)} />
          <Button variant="contained" onClick={onApplyFilters}>Apply</Button>
          <Box sx={{ flex: 1 }} />
          <Button variant="outlined" onClick={onExport}>Download CSV</Button>
        </Toolbar>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={240}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: studentColWidth, minWidth: studentColWidth, maxWidth: studentColWidth }}>
                  <Box display="flex" flexDirection="column">
                    <Typography variant="body2" fontWeight={700}>Students</Typography>
                    <Typography variant="caption" color="text.secondary">assessments ➝</Typography>
                  </Box>
                </TableCell>
                {assessments.map(a => (
                  <TableCell 
                    key={a.id} 
                    align="center"
                    sx={{ width: columnWidth, minWidth: columnWidth, maxWidth: columnWidth, px: 0.5 }}
                  >
                    <Tooltip title={`${a.title} (${dateFmt(a.date)})`}>
                      <Box sx={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Typography variant="caption" fontWeight={700} noWrap sx={{ fontSize: 10 }}>
                          {a.title}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>
                ))}
                {/* Filler column to push extra whitespace to the far right while retaining full-width rows */}
                <TableCell sx={{ p: 0 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={`${r.firstName}-${r.lastName}`}>
                  <TableCell 
                    sx={{ 
                      width: studentColWidth, minWidth: studentColWidth, maxWidth: studentColWidth, 
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' 
                    }}
                  >
                    {r.lastName}, {r.firstName}
                  </TableCell>
                  {assessments.map(a => {
                    const c = r.cells[a.id];
                    const display = (c && typeof c.score === 'number') ? `${c.score}%` : '—';
                    const color = c && typeof c.score === 'number'
                      ? (c.score >= 80 ? 'success.main' : c.score >= 60 ? 'warning.main' : 'error.main')
                      : 'text.secondary';
                    return (
                      <TableCell 
                        key={a.id} 
                        align="center" 
                        sx={{ color, width: columnWidth, minWidth: columnWidth, maxWidth: columnWidth, px: 0.5 }}
                      >
                        <Tooltip title={c ? `${a.title} • ${c.status || ''} • ${c.submittedAt ? new Date(c.submittedAt).toLocaleString() : ''}` : 'No submission'}>
                          <span style={{ cursor: c ? 'pointer' : 'default', fontSize: 11, lineHeight: 1 }} onClick={() => c && navigate(`/tests/grade/${a.id}`)}>
                            {display}
                          </span>
                        </Tooltip>
                      </TableCell>
                    );
                  })}
                  {/* Filler column to extend row background to the container's right edge */}
                  <TableCell sx={{ p: 0 }} />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default Gradebook;


